import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import MuxPlayer from "@mux/mux-player-react";
import {
  ChevronLeft, Building2, CalendarDays, TrendingUp, Loader2,
  Clock, AlertOctagon, ArrowUpRight,
} from "lucide-react";
import { HeartIcon, ShieldIcon } from "../lib/icons";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { CreateCampaign } from "../components/CreateCampaign";
import { DonateComingSoonDialog } from "../components/DonateComingSoonDialog";
import { FadeUp, CountUp } from "../components/Reveal";
import { PageAccent } from "../components/PageAccent";
import { EmSwash } from "../components/EmSwash";
import { PortraitFallback } from "../components/PortraitFallback";
import { PrototypeNotice } from "../components/PrototypeNotice";
import { PinnedThankYou } from "../components/PinnedThankYou";
import { LiveSupporterWall } from "../components/LiveSupporterWall";
import { ShareBar } from "../components/ShareBar";
import { useCampaign, campaignUrl, campaignShareUrl } from "../lib/campaign";
import { campaignSeo } from "../lib/campaignSeo";
import { SEO } from "../lib/SEO";
import NotFound from "./NotFound";
import { useAuth } from "../lib/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "mra_jwt";

const money = (cents) => `$${Math.round((cents || 0) / 100).toLocaleString()}`;

/**
 * daysBetween — inclusive whole-days until `iso`. Returns 0 for past dates.
 */
const daysUntil = (iso) => {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 0;
  const diff = t - Date.now();
  return Math.max(0, Math.ceil(diff / 86400000));
};

const fileUrl = (fileId, { publicRead = false } = {}) => {
  if (!fileId) return null;
  // For LIVE campaigns we intentionally omit the JWT — the file is
  // publicly readable server-side and dropping the token avoids leaking
  // it into <img src=> logs, analytics, or referrers.
  if (publicRead) return `${API}/api/files/${fileId}`;
  const token = typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null;
  const q = token ? `?auth=${token}` : "";
  return `${API}/api/files/${fileId}${q}`;
};

const Pill = ({ icon: Icon, children, testid }) => (
  <span
    data-testid={testid}
    className="inline-flex items-center gap-1.5 rounded-full bg-sand/95 px-3 py-1.5 text-[13px] font-semibold text-ink"
  >
    <Icon className="h-3.5 w-3.5 text-tangerine" strokeWidth={2} /> {children}
  </span>
);

const Card = ({ className = "", children, ...props }) => (
  <div className={`rounded-lg border border-border bg-bone ${className}`} {...props}>
    {children}
  </div>
);

const StatusBanner = ({ status, rejectionReason }) => {
  if (status === "paused") {
    return (
      <div data-testid="owner-paused-banner" className="mt-6 flex items-start gap-4 rounded-xl border-2 border-yellow/70 bg-yellow/15 p-5">
        <span className="mt-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-yellow/40 text-ink">
          <AlertOctagon className="h-5 w-5" strokeWidth={2.2} />
        </span>
        <div className="min-w-0">
          <p className="font-serif text-3xl text-ink">Paused by our team.</p>
          <p className="mt-1 text-base leading-relaxed text-olive">
            Your page is hidden from donors for the moment while we sort something out. Only you can see it. We&rsquo;ll email you — nothing you&rsquo;ve raised is affected.
          </p>
        </div>
      </div>
    );
  }
  if (status === "pending_review") {
    return (
      <div
        data-testid="owner-pending-banner"
        className="mt-6 flex items-start gap-4 rounded-xl border-2 border-cobalt/40 bg-cobalt/6 p-5"
      >
        <span className="mt-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-cobalt/15 text-cobalt">
          <ShieldIcon className="h-5 w-5" />
        </span>
        <div className="min-w-0">
          <p className="font-serif text-3xl text-ink">In review.</p>
          <p className="mt-1 text-base leading-relaxed text-olive">
            Our team is looking through your campaign. You'll get an email as soon as it goes live.
          </p>
        </div>
      </div>
    );
  }
  if (status === "rejected") {
    return (
      <div
        data-testid="owner-rejected-banner"
        className="mt-6 flex items-start gap-4 rounded-xl border-2 border-scarlet/40 bg-scarlet/6 p-5"
      >
        <span className="mt-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-scarlet/15 text-scarlet">
          <AlertOctagon className="h-5 w-5" strokeWidth={2.2} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-serif text-3xl text-ink">Our reviewer sent this back.</p>
          <p className="mt-1 text-base leading-relaxed text-olive">
            Head back to your dashboard to fix the notes below and resubmit.
          </p>
          {rejectionReason && (
            <blockquote
              data-testid="owner-rejection-reason"
              className="mt-3 rounded-md border-l-4 border-scarlet bg-bone px-4 py-3 font-serif text-base leading-relaxed text-ink"
            >
              "{rejectionReason}"
            </blockquote>
          )}
          <Link
            to="/dashboard"
            className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-ink px-4 py-2 text-[13px] font-semibold text-sand transition-colors hover:bg-forest"
            data-testid="owner-back-to-dashboard"
          >
            Back to dashboard
          </Link>
        </div>
      </div>
    );
  }
  if (status === "draft") {
    return (
      <div
        data-testid="owner-draft-banner"
        className="mt-6 rounded-xl border-2 border-ink/10 bg-bone p-5 text-[13px] text-olive"
      >
        This is a preview of your draft. Only you and our team can see it. Head back to your dashboard to keep editing.
      </div>
    );
  }
  return null;
};

const NotFoundState = ({ onOpenCreate, onOpenDonate }) => (
  <main className="bg-sand" data-testid="live-campaign-not-found">
    <Navbar onStart={onOpenCreate} onDonate={onOpenDonate} />
    <div className="mx-auto flex min-h-[70vh] max-w-3xl flex-col items-center justify-center px-6 text-center">
      <PageAccent className="mb-5" />
      <span className="text-[13px] font-semibold uppercase tracking-[0.22em] text-tangerine">
        Campaign not found
      </span>
      <h1 className="mt-4 font-serif text-3xl lg:text-[3.5rem] leading-[0.95] text-ink">
        This one&rsquo;s not <EmSwash>live yet.</EmSwash>
      </h1>
      <p className="mt-5 max-w-md text-base leading-relaxed text-olive">
        The link might be old, the campaign might still be in review, or it may have already closed. Browse everyone raising right now instead.
      </p>
      <Link
        to="/"
        className="mt-8 inline-flex items-center gap-1.5 rounded-full bg-ink px-6 py-3 text-base font-semibold text-sand transition-colors hover:bg-forest"
        data-testid="not-found-back-home"
      >
        Back to My Rich Aunt
      </Link>
    </div>
    <Footer onStart={onOpenCreate} onDonate={onOpenDonate} />
  </main>
);

const LoadingState = () => (
  <main className="bg-sand" data-testid="live-campaign-loading">
    <Navbar onStart={() => {}} onDonate={() => {}} />
    <div className="flex min-h-[70vh] items-center justify-center">
      <Loader2 className="h-6 w-6 animate-spin text-olive" />
    </div>
    <Footer onStart={() => {}} onDonate={() => {}} />
  </main>
);

export default function LiveCampaign() {
  const { id, slug } = useParams();
  const { campaign, loading, error } = useCampaign(id, slug);
  const { user } = useAuth();

  const [createOpen, setCreateOpen] = useState(false);
  const [donateOpen, setDonateOpen] = useState(false);

  const daysLeft = useMemo(() => daysUntil(campaign?.ends_at), [campaign?.ends_at]);

  if (loading) return <LoadingState />;

  if (error === "not_found" || !campaign) {
    // A stray one-segment URL (/whatever) lands here via the vanity route — show the real 404.
    if (slug) return <NotFound />;
    return (
      <NotFoundState
        onOpenCreate={() => setCreateOpen(true)}
        onOpenDonate={() => setDonateOpen(true)}
      />
    );
  }

  const isOwner = user && user.user_id === campaign.student_id;
  const isLive = campaign.status === "live";
  const isClosed = campaign.status === "closed";
  const isPublicViewable = isLive || isClosed || isOwner || user?.is_admin;
  if (!isPublicViewable) {
    return (
      <NotFoundState
        onOpenCreate={() => setCreateOpen(true)}
        onOpenDonate={() => setDonateOpen(true)}
      />
    );
  }

  const rawGoal = campaign.goal_cents || 0;
  const rawRaised = campaign.raised_cents || 0;
  const pct = rawGoal > 0 ? Math.round((rawRaised / rawGoal) * 100) : 0;
  const stillNeededCents = Math.max(rawGoal - rawRaised, 0);

  const displayName = campaign.student_name || "This student";
  const coverSrc = fileUrl(campaign.cover_file_id, { publicRead: isLive || isClosed });
  const introVideo = campaign.intro_video;
  const videoReady = introVideo && introVideo.status === "ready" && introVideo.playback_id;

  return (
    <main data-testid="live-campaign-page" className="bg-sand">
      {/* Public pages get per-campaign title/description/og:image; owner/admin
          previews of unpublished pages must never be indexed. */}
      {isLive || isClosed ? (
        <SEO {...campaignSeo(campaign)} />
      ) : (
        <meta name="robots" content="noindex, nofollow" />
      )}
      <Navbar onStart={() => setCreateOpen(true)} onDonate={() => setDonateOpen(true)} />

      <div className="mx-auto max-w-7xl px-6 pt-36 pb-24 lg:px-8 lg:pt-44">
        <FadeUp tier="sm">
          <PageAccent className="mb-5" />
        </FadeUp>
        <Link
          to="/"
          data-testid="back-to-home"
          className="inline-flex items-center gap-1.5 text-base font-medium text-olive transition-colors hover:text-ink"
        >
          <ChevronLeft className="h-4 w-4" /> Back to My Rich Aunt
        </Link>

        {isOwner && !isLive && !isClosed && (
          <StatusBanner
            status={campaign.status}
            rejectionReason={campaign.rejection_reason}
          />
        )}
        {isClosed && (
          <div
            data-testid="campaign-ended-banner"
            className="mt-6 flex flex-col gap-3 rounded-lg border border-ink/15 bg-bone p-5 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="flex items-start gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-ink text-sand"><CalendarDays className="h-4 w-4" strokeWidth={2} /></span>
              <div>
                <p className="text-[13px] font-semibold uppercase tracking-[0.22em] text-olive">This campaign has ended</p>
                <p className="mt-1 text-[13px] text-ink">
                  <strong>${Math.round(rawRaised / 100).toLocaleString()}</strong> of ${Math.round(rawGoal / 100).toLocaleString()} was given to My Rich Aunt with {displayName} in mind, from {campaign.donor_count || 0} supporter{(campaign.donor_count || 0) === 1 ? "" : "s"}. Grants are still going out on the weekly schedule.
                </p>
              </div>
            </div>
            <Link to="/campaigns" data-testid="campaign-ended-browse" className="inline-flex shrink-0 items-center gap-2 rounded-full bg-ink px-5 py-2.5 text-[13px] font-semibold text-sand hover:bg-forest">
              Students raising now <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2} />
            </Link>
          </div>
        )}

        <PinnedThankYou thankYou={campaign.latest_thank_you} studentName={displayName} />

        <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-3 lg:gap-10">
          {/* Left column — story + media */}
          <div className="lg:col-span-2">
            {/* Title + byline shown above the hero when there's no cover
                (the cover overlay carries them otherwise). */}
            {!coverSrc && (
              <FadeUp>
                <p
                  className="text-[13px] font-semibold uppercase tracking-[0.22em] text-tangerine"
                  data-testid="live-campaign-byline"
                >
                  By {displayName}
                </p>
                <h1
                  className="mt-3 font-serif text-3xl lg:text-[3.5rem] leading-[1.02] tracking-tight text-ink"
                  data-testid="live-campaign-title"
                >
                  {campaign.title || `${displayName}'s campaign`}
                </h1>
              </FadeUp>
            )}

            {/* Hero */}
            <FadeUp>
              <div
                className={`relative overflow-hidden rounded-lg border border-border ${!coverSrc ? "mt-6" : ""}`}
                data-testid="live-campaign-hero"
              >
                {coverSrc ? (
                  <>
                    <img
                      src={coverSrc}
                      alt={campaign.title || `${displayName}'s campaign`}
                      className="h-72 w-full object-cover sm:h-96"
                      data-testid="live-campaign-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/25 to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 p-6 lg:p-8">
                      <h1
                        className="font-serif text-3xl lg:text-[3.5rem] leading-[1.02] tracking-tight text-sand"
                        data-testid="live-campaign-title"
                      >
                        {campaign.title || `${displayName}'s campaign`}
                      </h1>
                      <p className="mt-3 text-sand/85" data-testid="live-campaign-byline">
                        By <span className="font-semibold text-sand">{displayName}</span>
                      </p>
                      <div className="mt-4 flex flex-wrap gap-2.5">
                        {campaign.school && (
                          <Pill icon={Building2} testid="hero-school-pill">
                            {campaign.school}
                          </Pill>
                        )}
                        {campaign.category && (
                          <Pill icon={TrendingUp} testid="hero-category-pill">
                            {campaign.category}
                          </Pill>
                        )}
                        {isLive && daysLeft > 0 && (
                          <Pill icon={Clock} testid="hero-days-pill">
                            {daysLeft} {daysLeft === 1 ? "day" : "days"} left{daysLeft <= 7 ? " · ending soon" : ""}
                          </Pill>
                        )}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="h-72 w-full sm:h-96">
                    <PortraitFallback
                      name={displayName}
                      school={campaign.school || ""}
                      gradYear=""
                      nameSize="text-[clamp(4rem,11vw,9rem)]"
                    />
                  </div>
                )}
              </div>

              {!isOwner && <PrototypeNotice testid="live-campaign-prototype-notice" className="mt-4" />}

              {!coverSrc && (
                <div className="mt-4 flex flex-wrap items-center gap-2.5">
                  {campaign.school && (
                    <Pill icon={Building2} testid="fallback-school-pill">
                      {campaign.school}
                    </Pill>
                  )}
                  {campaign.category && (
                    <Pill icon={TrendingUp} testid="fallback-category-pill">
                      {campaign.category}
                    </Pill>
                  )}
                </div>
              )}
            </FadeUp>

            {campaign.title && campaign.story && (
              <FadeUp>
                <h2 className="mt-10 font-serif text-3xl text-ink">
                  {displayName}&rsquo;s story
                </h2>
              </FadeUp>
            )}

            {/* About the student — headline + bio from the builder */}
            {(campaign.headline || campaign.bio) && (
              <FadeUp>
                <div className="mt-6 max-w-2xl" data-testid="live-campaign-about-card">
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <span className="text-[13px] font-semibold uppercase tracking-[0.22em] text-tangerine">About {displayName}</span>
                    {(campaign.program || campaign.grad_year) && (
                      <span className="text-[13px] text-olive" data-testid="live-campaign-program">
                        {[campaign.program, campaign.grad_year ? `Class of \u2019${String(campaign.grad_year).slice(-2)}` : null].filter(Boolean).join(" · ")}
                      </span>
                    )}
                  </div>
                  {campaign.headline && (
                    <p className="mt-4 font-serif text-3xl italic leading-snug text-ink" data-testid="live-campaign-headline">&ldquo;{campaign.headline}&rdquo;</p>
                  )}
                  {campaign.bio && (
                    <p className="mt-5 whitespace-pre-line text-base leading-relaxed text-olive" data-testid="live-campaign-bio">{campaign.bio}</p>
                  )}
                </div>
              </FadeUp>
            )}

            {/* Story */}
            {campaign.story && (
              <FadeUp>
                <div className="mt-12 max-w-2xl" data-testid="live-campaign-story-card">
                  <span className="text-[13px] font-semibold uppercase tracking-[0.22em] text-olive">The need</span>
                  <p
                    data-testid="live-campaign-story"
                    className="mt-4 whitespace-pre-line text-base leading-relaxed text-ink/85"
                  >
                    {campaign.story}
                  </p>
                </div>
              </FadeUp>
            )}

            {/* Photo gallery */}
            {campaign.photo_file_ids?.length > 0 && (
              <FadeUp>
                <div className="mt-12" data-testid="live-campaign-gallery-card">
                  <h3 className="font-serif text-3xl text-ink">A few photos</h3>
                  <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
                    {campaign.photo_file_ids.map((id) => (
                      <div key={id} className="aspect-square overflow-hidden rounded-md" data-testid="live-campaign-photo">
                        <img src={fileUrl(id, { publicRead: campaign.status === "live" })} alt="" className="h-full w-full object-cover" loading="lazy" />
                      </div>
                    ))}
                  </div>
                </div>
              </FadeUp>
            )}

            {/* Intro video */}
            {videoReady && (
              <FadeUp>
                <div className="mt-12" data-testid="live-campaign-video-card">
                  <h3 className="font-serif text-3xl text-ink">Meet {displayName}</h3>
                  <div className="mt-5 overflow-hidden rounded-md bg-ink">
                    <MuxPlayer
                      data-testid="live-campaign-video"
                      playbackId={introVideo.playback_id}
                      streamType="on-demand"
                      poster={introVideo.poster_url}
                      thumbnailTime={1}
                      accentColor="#FD6106"
                      metadata={{
                        video_title: campaign.title || `${displayName}'s intro`,
                      }}
                      className="aspect-video w-full"
                    />
                  </div>
                </div>
              </FadeUp>
            )}

            {/* Supporter Wall — real, Mongo-backed (stays readable after the campaign ends) */}
            {(isLive || isClosed) && (
              <FadeUp>
                <div className="mt-14">
                  <LiveSupporterWall
                    campaignId={campaign.id}
                    studentFirstName={displayName}
                    readOnly={isClosed}
                    onOpenDonate={() => setDonateOpen(true)}
                  />
                </div>
              </FadeUp>
            )}
          </div>

          {/* Right column — Progress + Donate */}
          <div className="lg:col-span-1">
            <div className="space-y-6 lg:sticky lg:top-28">
              <FadeUp>
                <Card className="p-6 lg:p-7" data-testid="live-campaign-progress-card">
                  {daysLeft > 0 && daysLeft <= 14 && (
                    <motion.div
                      data-testid="live-urgency-pill"
                      initial={{ opacity: 0, y: -6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                      className="mb-5 inline-flex items-center gap-2 rounded-full bg-scarlet/10 px-3.5 py-1.5 text-[13px] font-semibold text-scarlet"
                    >
                      <motion.span
                        className="flex h-2 w-2 rounded-full bg-scarlet"
                        animate={{ scale: [1, 1.4, 1], opacity: [1, 0.5, 1] }}
                        transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                      />
                      Only {daysLeft} {daysLeft === 1 ? "day" : "days"} left
                    </motion.div>
                  )}

                  <div className="flex items-end justify-between">
                    <div>
                      <p
                        className="font-serif text-3xl leading-none text-ink"
                        data-testid="live-raised-amount"
                      >
                        <CountUp
                          key={rawRaised}
                          startOnMount
                          from={0}
                          to={Math.round(rawRaised / 100)}
                          prefix="$"
                          duration={1.2}
                        />
                      </p>
                      <p className="mt-1 text-[13px] uppercase tracking-[0.15em] text-olive">Given so far</p>
                    </div>
                    <div className="text-right">
                      <p
                        className="font-serif text-3xl leading-none text-ink"
                        data-testid="live-goal-amount"
                      >
                        {money(rawGoal)}
                      </p>
                      <p className="mt-1 text-[13px] uppercase tracking-[0.15em] text-olive">Goal</p>
                    </div>
                  </div>

                  <div className="mt-5 h-3 w-full overflow-hidden rounded-full bg-secondary">
                    <motion.div
                      data-testid="live-progress-bar"
                      className="h-full rounded-full bg-yellow"
                      initial={{ width: 0 }}
                      animate={{ width: `${Math.min(pct, 100)}%` }}
                      transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                    />
                  </div>

                  <p className="mt-3 flex flex-wrap items-center gap-x-2 text-[13px] text-olive" data-testid="live-progress-meta">
                    <span data-testid="live-stat-progress">{pct}% funded</span>
                    <span aria-hidden="true">·</span>
                    <span data-testid="live-still-needed">{money(stillNeededCents)} to go</span>
                    <span aria-hidden="true">·</span>
                    <span data-testid="live-stat-days">
                      {isClosed
                        ? `Ended ${new Date(campaign.closed_at || campaign.ends_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`
                        : `${daysLeft} ${daysLeft === 1 ? "day" : "days"} left`}
                    </span>
                  </p>

                  {isLive ? (
                    <button
                      type="button"
                      data-testid="live-donate-button"
                      onClick={() => setDonateOpen(true)}
                      className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-ink px-6 py-4 text-base font-semibold text-sand transition-colors hover:bg-forest"
                    >
                      <HeartIcon className="h-4 w-4" /> Donate now
                    </button>
                  ) : (
                    <div
                      data-testid="live-donate-disabled"
                      className="mt-5 rounded-full bg-border/50 px-6 py-4 text-center text-[13px] font-semibold text-olive"
                    >
                      {isClosed ? "This campaign has ended. Thank you to everyone who gave." : "Donations open once this campaign is live."}
                    </div>
                  )}

                  <p className="mt-3 text-center text-[13px] leading-relaxed text-olive">
                    100% tax-deductible. My Rich Aunt is a 501(c)(3) nonprofit.
                  </p>

                  {isLive && (
                    <div className="mt-8 border-t border-border pt-6" data-testid="live-share-slot">
                      <p className="text-[13px] font-semibold uppercase tracking-[0.22em] text-olive">
                        Share this campaign
                      </p>
                      <p className="mt-1.5 text-[13px] leading-relaxed text-olive">
                        Every text, DM, and story you send starts real support.
                      </p>
                      <div className="mt-3.5">
                        <ShareBar
                          url={campaignShareUrl(campaign)}
                          displayUrl={typeof window !== "undefined"
                            ? campaignUrl(campaign)
                            : `/campaign/${campaign.id}`}
                          studentFirstName={displayName}
                          campaignTitle={campaign.title}
                          goalCents={rawGoal}
                          variant="light"
                          testidPrefix="live-share"
                        />
                      </div>
                    </div>
                  )}
                </Card>
              </FadeUp>
            </div>
          </div>
        </div>
      </div>


      <Footer onStart={() => setCreateOpen(true)} onDonate={() => setDonateOpen(true)} />

      <CreateCampaign open={createOpen} onOpenChange={setCreateOpen} />
      <DonateComingSoonDialog
        open={donateOpen}
        onOpenChange={setDonateOpen}
        studentName={displayName}
        campaignId={campaign?.id || campaign?.campaign_id}
      />
    </main>
  );
}
